import {
  BlurFilter,
  Container,
  Graphics,
  PerspectiveMesh,
  RenderTexture,
  Sprite,
  type Renderer,
} from 'pixi.js';
import { SIGNAL } from './palette';
import { keepAlive } from './textureLifetime';
import { CARD_HEIGHT, CARD_WIDTH, type CardView } from './card';

/**
 * **卡牌层的占地与外发光**。
 *
 * 评审要求：*「在标量场和卡牌层都计算完毕后，将整个卡牌层计算一次
 * "外发光式"的阴影，并使得被卡牌层覆盖的所有位置上标量场置零。
 * 卡牌可能有异形渲染，但置零总是默认渲染的圆角矩形。」*
 *
 * ## 这一步在做什么
 *
 * ```
 *   ① 把每张牌的「默认轮廓」画进一张遮罩贴图        ← 圆角矩形，不是牌面实际像素
 *        │
 *        ├─▶ 给背景的标量场用：被覆盖的地方乘 0（挖个洞）
 *        │
 *        └─▶ 挂上模糊滤镜 → 外发光（垫在卡牌下面）
 * ```
 *
 * ## 为什么用「默认轮廓」而不是卡牌实际渲染的像素
 *
 * 这个选择是对的，不只是省事：
 *
 * - 牌面是 shader 画的，可能带透明、可能伸出框外。拿实际像素当遮罩，
 *   标量场会顺着花纹"漏"出来 —— 看起来像渲染 bug，不像设计
 * - 默认轮廓是**稳定**的：换一套牌面美术，挖的洞不该跟着变
 * - 轮廓极便宜（一个圆角矩形），而且能跟着姿态任意变形
 *
 * 轮廓复用 `CardView.corners` —— **和卡牌本身用的是同一份四角**，
 * 所以挖出来的洞不可能和牌对不上（倾斜的牌，洞也是斜的）。
 *
 * ## 外发光怎么做
 *
 * 把遮罩**模糊**一份，垫在卡牌层底下，**加色混合**。
 * 卡牌自己不透明，中间那坨会被盖住，露出来的正好是一圈外发光。
 *
 * 比"模糊再减去原图"省一步，也不需要算距离场 —— 而且效果一样，
 * 因为"减掉原图"这件事本来就由卡牌自己完成了。
 *
 * ⚠️ 必须是**加色**。黑底上加法才有"发亮"的感觉，
 * 普通 alpha 混合会变成一层灰雾，把黑底弄脏。
 */

/**
 * 遮罩贴图的分辨率倍率。
 *
 * 它只回答"哪里被盖住了"，而且马上要被模糊掉 —— 不需要屏幕分辨率。
 */
const MASK_SCALE = 1;

/** 轮廓网格的细分数。透视下圆角会被拉，2×2 不够；8×8 已经看不出折线 */
const SILHOUETTE_SEGMENTS = 8;

export interface CardFootprintOptions {
  /** 逻辑画布尺寸 */
  readonly width: number;
  readonly height: number;
  /** 外发光的扩散半径（逻辑像素） */
  readonly glowRadius?: number;
  /** 外发光的强度 */
  readonly glowStrength?: number;
  /** 外发光的颜色 */
  readonly glowColor?: number;
}

export class CardFootprint {
  /** 占地遮罩。背景的标量场采样它来挖洞 */
  readonly mask: RenderTexture;

  /** 加到场景里的外发光层。**垫在卡牌下面**（加色混合） */
  readonly view: Sprite;

  readonly #stage = new Container();
  readonly #blur: BlurFilter;
  /** 共用的圆角矩形轮廓。所有卡牌长宽比一样，所以只做一张 */
  readonly #silhouette: RenderTexture;

  constructor(renderer: Renderer, options: CardFootprintOptions) {
    this.mask = keepAlive(
      RenderTexture.create({
        width: Math.max(1, Math.round(options.width * MASK_SCALE)),
        height: Math.max(1, Math.round(options.height * MASK_SCALE)),
        resolution: 1,
        label: 'card-footprint-mask',
      }),
    );

    this.#silhouette = makeSilhouette(renderer);

    this.#blur = new BlurFilter({
      strength: options.glowRadius ?? 16,
      quality: 4,
      // ⚠️ padding 必须给足，否则光晕会被自己的贴图边界切掉，变成方角
      padding: (options.glowRadius ?? 16) * 2,
    });

    // 直接显示遮罩、挂模糊 —— 不用再开一张 RT 手动跑第二遍。
    // 着色器之外的事情尽量交给 Pixi 自带的滤镜，它们比手写的好也快
    this.view = new Sprite(this.mask);
    this.view.label = 'card-glow';
    this.view.tint = options.glowColor ?? SIGNAL.accent;
    this.view.alpha = options.glowStrength ?? 0.75;
    // ⚠️ 加色：黑底上加法才发亮
    this.view.blendMode = 'add';
    this.view.filters = [this.#blur];
  }

  /**
   * 重新算一遍占地。
   *
   * ⚠️ **必须在主渲染之前调用**（挂到那个唯一的 ticker 回调里）。
   * 背景的标量场着色器要采样 `mask`，遮罩必须先画好 ——
   * 否则背景用的是上一帧的洞，卡牌一动就会看到"影子跟不上牌"。
   */
  update(renderer: Renderer, cards: readonly CardView[]): void {
    this.#stage.removeChildren();

    for (const card of cards) {
      if (!card.visible || card.alpha <= 0.001) continue;
      // 复用卡牌自己算出来的四个角 —— 挖的洞和牌**不可能对不上**
      const silhouette = new PerspectiveMesh({
        texture: this.#silhouette,
        verticesX: SILHOUETTE_SEGMENTS,
        verticesY: SILHOUETTE_SEGMENTS,
        ...card.corners,
      });
      silhouette.position = card.position;
      silhouette.scale = card.scale;
      this.#stage.addChild(silhouette);
    }

    renderer.render({ container: this.#stage, target: this.mask, clear: true });
  }

  destroy(): void {
    this.#stage.destroy({ children: true });
    this.#blur.destroy();
    this.#silhouette.destroy(true);
    this.mask.destroy(true);
    this.view.destroy();
  }
}

/**
 * 一张**白色圆角矩形印在透明底上**的贴图。
 *
 * 它就是"默认渲染的圆角矩形"这个约定的**本体**。
 * 圆角半径和 `card/art/plain.ts` 里的牌面保持一致，否则挖的洞会比牌小一圈。
 */
function makeSilhouette(renderer: Renderer): RenderTexture {
  const width = Math.round(CARD_WIDTH);
  const height = Math.round(CARD_HEIGHT);
  const rt = keepAlive(RenderTexture.create({ width, height, resolution: 1, label: 'card-silhouette' }));
  const shape = new Graphics()
    .roundRect(0, 0, width, height, width * 0.07)
    .fill({ color: 0xffffff });
  renderer.render({ container: shape, target: rt, clear: true });
  shape.destroy();
  return rt;
}
